import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon } from "lucide-react";
import { resolveImage } from "@/lib/format";

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", price: "0", category: "ebooks",
    image_url: "", file_path: "", file_name: "", featured: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || []));
    if (isEdit) {
      api.get("/admin/products").then((r) => {
        const p = (r.data || []).find((x) => x.id === id);
        if (p) setForm({
          title: p.title || "", description: p.description || "", price: String(p.price || 0),
          category: p.category || "ebooks", image_url: p.image_url || "",
          file_path: p.file_path || "", file_name: p.file_name || "",
          featured: Boolean(p.featured),
        });
      });
    }
  }, [id, isEdit]);

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    // IMPORTANT: do NOT manually set Content-Type. The browser must add
    // "multipart/form-data; boundary=…" itself; a manual header strips the
    // boundary and makes the server reject the upload.
    const res = await api.post(`/admin/upload?kind=${kind}`, fd);
    return res.data;
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const r = await uploadFile(file, "image");
      setForm((f) => ({ ...f, image_url: r.url }));
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Image upload failed";
      toast.error(typeof detail === "string" ? detail : "Image upload failed");
    } finally {
      // Allow re-uploading the same file by resetting the input value
      try { e.target.value = ""; } catch (_) {}
    }
  };
  const handleDigital = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const r = await uploadFile(file, "file");
      setForm((f) => ({ ...f, file_path: r.path, file_name: r.filename }));
      toast.success("Digital file uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "File upload failed";
      toast.error(typeof detail === "string" ? detail : "File upload failed");
    } finally {
      try { e.target.value = ""; } catch (_) {}
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const price = Number(form.price);
    if (!(price >= 0)) { toast.error("Price must be a number"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price };
      if (isEdit) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl" data-testid="product-form-page">
      <Link to="/admin/products" className="text-xs text-neutral-500 underline">← Back to products</Link>
      <h1 className="editorial font-serif-display text-4xl mt-4">{isEdit ? "Edit product" : "New product"}</h1>

      <div className="mt-10 space-y-6 bg-white border border-black/10 rounded-sm p-6">
        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-500">Title</Label>
          <Input className="mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 2026 Monthly Goals Planner" data-testid="form-title" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-500">Description</Label>
          <Textarea className="mt-2 min-h-32" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's inside? Who is it for?" data-testid="form-description" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Price (USD)</Label>
            <Input className="mt-2" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-2" data-testid="form-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => (<SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UploadBox
            label="Product image"
            icon={<ImageIcon className="h-5 w-5" />}
            preview={form.image_url ? <img src={resolveImage(form.image_url)} alt="" className="w-full h-full object-cover" /> : null}
            onChange={handleImage}
            inputId="prod-image"
            testid="upload-image"
          />
          <UploadBox
            label="Digital file (PDF, zip, …)"
            icon={<FileText className="h-5 w-5" />}
            preview={form.file_name ? <div className="text-xs text-neutral-700 px-3 text-center">{form.file_name}</div> : null}
            onChange={handleDigital}
            inputId="prod-file"
            testid="upload-file"
          />
        </div>

        <div className="flex items-center justify-between border border-black/10 rounded-sm px-4 py-3">
          <div>
            <div className="font-medium text-sm">Feature on homepage</div>
            <div className="text-xs text-neutral-500">Shown in the Featured row.</div>
          </div>
          <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
        </div>

        <div className="pt-2 flex gap-3">
          <button onClick={save} disabled={saving} className="pill-btn h-11 px-6 rounded-full bg-black text-white text-sm hover:bg-neutral-800 disabled:opacity-50" data-testid="save-product-btn">
            {saving ? "Saving…" : "Save product"}
          </button>
          <Link to="/admin/products" className="pill-btn h-11 px-6 rounded-full border border-black/15 text-sm hover:bg-neutral-100 inline-flex items-center">Cancel</Link>
        </div>
      </div>
    </div>
  );
}

function UploadBox({ label, icon, preview, onChange, inputId, testid }) {
  return (
    <label htmlFor={inputId} className="block cursor-pointer" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div className="aspect-[4/3] border border-dashed border-black/20 rounded-sm bg-[#F3F2F0] flex items-center justify-center overflow-hidden">
        {preview || (
          <div className="text-center text-neutral-500 flex flex-col items-center gap-2">
            {icon}
            <span className="text-xs flex items-center gap-1"><Upload className="h-3 w-3" />Click to upload</span>
          </div>
        )}
      </div>
      <input id={inputId} type="file" className="hidden" onChange={onChange} />
    </label>
  );
}
